import { Vector2 } from "../../common/vector2.js";
import { ImgID } from "../../image_loader/img_id.js";
import { Easing } from "../../transforms/transform_base.js";
import { TransformScale } from "../../transforms/transform_scale.js";
import { setButtonTextOutline } from "../common_ui_elements/button_text_outline.js";
import { ColorScheme } from "../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../common_ui_elements/text_style_scheme.js";
import { UIAdaptiveElementsByTextHorisontal } from "../common_ui_elements/ui_adaptive_elements_by_text_horisontal.js";
import { UIElement } from "../common_ui_elements/ui_element.js";
import { UIElementHolder } from "../common_ui_elements/ui_element_holder.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../common_ui_elements/ui_nine_slice_element.js";
import { addMirroredDialogBg } from "../common_ui_elements/mirrored_dialog_bg.js";
import { DialogCoinsBadge } from "../common_ui_elements/dialog_coins_badge.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../common_ui_elements/ui_text_element.js";
import { AbstractDialog } from "./abstract_dialog.js";
import { DialogManager } from "./dialog_manager.js";
import { showRewarded } from "../../ads/rewarded.js";
import { areAdsBlockedOffline } from "../../ads/offline_ads.js";
import { hideOfflineAdTooltip, showAdUnavailableTooltip, showOfflineAdTooltip } from "../tooltip/offline_ad_tooltip.js";
import {
  isRewardedCooldownActive,
  rewardedCooldownRemainingMs,
  rewardedCooldownTimerText,
  startRewardedCooldown,
  type RewardedCooldownId,
} from "../../ads/rewarded_booster_cooldown.js";
import {
  logBoosterRewardedFunnelEvent,
  logRewardedCompleted,
} from "../../analytics/appsflyer_events.js";
import { AnalyticsEventName } from "../../analytics/index.js";
import { rewardBooster } from "../../offline/economy_gateway.js";
import { adoptEconomyFromPlayer } from "../../api/player_sync.js";
import type { RewardBoosterResponse, RewardBoosterType } from "../../api/dto/player.dto.js";
import type { BoosterPriceOfferDto, PurchaseBoosterResponse } from "../../api/dto/cumulative_boosters.dto.js";
import { CumulativeBoosterID } from "../../cumulative_boosters/cumulative_booster_id.js";
import { CumulativeBoosterService } from "../../cumulative_boosters/cumulative_booster_service.js";
import { StatisticsStorage } from "../../storage/statistics_storage.js";
import { OverlayFactory } from "../overlay/overlay_factory.js";
import { OverlayId } from "../overlay/overlay_ids.js";
import { LocalizationManager } from "../../localization/localization_manager.js";
import {
  BuyBoosterType,
  BuyBoosterRewardSource,
  getDefaultBoosterEffectValue,
  getBoosterTextVariableParams,
} from "./buy_booster_types.js";

export interface BuyBoosterDialogOptions
{
  booster_type: BuyBoosterType;
  booster_id: CumulativeBoosterID;
  offer: BoosterPriceOfferDto;
  source: BuyBoosterRewardSource;
  on_buy?: ( response: PurchaseBoosterResponse ) => void;
  on_reward?: ( quantity: number ) => void;
}

function getRewardedCooldownId( booster_type: BuyBoosterType ): RewardedCooldownId
{
  switch ( booster_type )
  {
    case BuyBoosterType.Firework: return 'firework';
    case BuyBoosterType.FiveBalls: return 'tenballs';
    case BuyBoosterType.BigBall: return 'bigball';
    case BuyBoosterType.InfiniteShots: return 'infiniteballs';
  }
}


function splitIntoTwoBalancedLines( text: string ): string
{
  if ( text.includes( "\n" ) ) return text;
  const words = text.trim().split( /\s+/ );
  if ( words.length < 2 ) return text;

  let split_at = 1;
  let smallest_difference = Number.POSITIVE_INFINITY;
  for ( let i = 1; i < words.length; i++ )
  {
    const difference = Math.abs(
      words.slice( 0, i ).join( " " ).length - words.slice( i ).join( " " ).length
    );
    if ( difference < smallest_difference )
    {
      smallest_difference = difference;
      split_at = i;
    }
  }

  return `${words.slice( 0, split_at ).join( " " )}\n${words.slice( split_at ).join( " " )}`;
}

interface BuyBoosterVisualConfig
{
  title_key: string;
  description_key: string;
  icon_id: ImgID;
  analytics_id: RewardBoosterType;
}

const BOOSTER_VISUAL_CONFIGS: Record<BuyBoosterType, BuyBoosterVisualConfig> = {
  [BuyBoosterType.Firework]: {
    title_key: "BOOSTER.FIREWORK",
    description_key: "DESCRIPTION.FIREWORK",
    icon_id: ImgID.UI_GameStartFireworkBooster,
    analytics_id: "firework",
  },
  [BuyBoosterType.FiveBalls]: {
    title_key: "BOOSTER.FIVE_BALLS",
    description_key: "DESCRIPTION.FIVE_BALLS",
    icon_id: ImgID.UI_GameStartMultiballsBooster,
    analytics_id: "five_balls",
  },
  [BuyBoosterType.BigBall]: {
    title_key: "BOOSTER.BIG_BALL",
    description_key: "DESCRIPTION.BIG_BALL",
    icon_id: ImgID.UI_GameStartBigBallBooster,
    analytics_id: "big_ball",
  },
  [BuyBoosterType.InfiniteShots]: {
    title_key: "BOOSTER.INFINITE_SHOTS",
    description_key: "DESCRIPTION.INFINITE_SHOTS",
    icon_id: ImgID.UI_GameStartInfiniteBallsBooster,
    analytics_id: "infinite_shots",
  },
};

const ROCKET_VISUAL_CONFIG: BuyBoosterVisualConfig = {
  title_key: "PASSIVE.BOOSTER.ROCKET",
  description_key: "DESCRIPTION.ROCKET",
  icon_id: ImgID.UI_PassiveBoosterRocket,
  analytics_id: "rocket",
};

const MINE_VISUAL_CONFIG: BuyBoosterVisualConfig = {
  title_key: "PASSIVE.BOOSTER.MINES",
  description_key: "DESCRIPTION.MINES",
  icon_id: ImgID.UI_PassiveBoosterBomb,
  analytics_id: "mines",
};

export class BuyBoosterDialog extends AbstractDialog
{
  private static readonly DESIGN_W = 1080;
  private static readonly DESIGN_H = 1944;
  private static readonly TITLE_FONT_DESIGN = 70;
  private static readonly TITLE_OUTLINE_DESIGN = 14;
  /** Top of the first line of a two-line title, a bit above centre of the banner face. */
  private static readonly TWO_LINE_TITLE_TOP_DESIGN = 27;

  // Exact transform from the 1080 x 1944 design.
  private static readonly BG_X = 62;
  private static readonly BG_Y = 413;
  private static readonly BG_W = 956;
  private static readonly BG_H = 1365;
  private static readonly HIT_X = 62;
  private static readonly HIT_Y = 391;
  private static readonly HIT_W = 956;
  private static readonly HIT_H = 1387;
  private static readonly BTN_W = 696;
  private static readonly BTN_H = 265;
  private static readonly BTN_X = ( BuyBoosterDialog.DESIGN_W - BuyBoosterDialog.BTN_W ) / 2;
  private static readonly REWARD_BTN_Y = 1182;
  private static readonly BUY_BTN_Y = 1432;

  private price: number;
  private quantity: number;
  private booster_id: CumulativeBoosterID;
  private offer_id: number;
  private on_buy?: ( response: PurchaseBoosterResponse ) => void;
  private on_reward?: ( quantity: number ) => void;
  private booster_type: BuyBoosterType;
  private effect_value: number | null;
  private readonly reward_quantity = 1;
  private reward_source: BuyBoosterRewardSource;
  private rewarded_placement: string;
  private rewarded_cooldown_id: RewardedCooldownId;
  private reward_available: boolean;
  private visual_config: BuyBoosterVisualConfig;
  private purchase_inflight = false;
  private close_btn!: UIImage;
  private coins_badge: DialogCoinsBadge | null = null;
  private reward_inflight = false;
  private reward_offer_shown_tracked = false;
  private reward_offer_clicked = false;
  private reward_granted = false;
  private reward_offer_closed_tracked = false;
  private reward_btn: UINineSliceElement | null = null;
  private reward_active_content: UIElement | null = null;
  private reward_cooldown_content: UIElement | null = null;
  private reward_cooldown_text: UITextElement | null = null;
  private reward_text: UITextElement | null = null;
  private shown_reward_inactive: boolean | null = null;
  private reward_cooldown_visible = false;
  private shown_reward_cooldown_timer = "";
  private reward_debug_render_reason: string | null = null;
  private next_reward_sync_at = 0;
  private shown_ads_offline = false;

  constructor( options: BuyBoosterDialogOptions )
  {
    const body_w = LayoutScaler.widthFromDesign( BuyBoosterDialog.DESIGN_W );
    const body_h = LayoutScaler.heightFromDesign( BuyBoosterDialog.DESIGN_H );
    const body_x = ( DialogManager.getWidth() - body_w ) / 2;
    const body_y = ( DialogManager.getHeight() - body_h ) / 2;

    super( DialogManager.ctx, body_x, body_y, body_w, body_h );

    this.price = options.offer.price;
    this.quantity = options.offer.amount;
    this.booster_id = options.booster_id;
    this.offer_id = options.offer.id;
    this.on_buy = options.on_buy;
    this.on_reward = options.on_reward;
    this.booster_type = options.booster_type;
    this.visual_config = options.booster_id === CumulativeBoosterID.Mine
      ? MINE_VISUAL_CONFIG
      : options.booster_id === CumulativeBoosterID.Dynamite
        || options.booster_id === CumulativeBoosterID.Rocket
        ? ROCKET_VISUAL_CONFIG
        : BOOSTER_VISUAL_CONFIGS[options.booster_type];
    this.reward_source = options.source;
    this.rewarded_placement = `booster_${this.reward_source}_${this.visual_config.analytics_id}`;
    this.rewarded_cooldown_id = getRewardedCooldownId( this.booster_type );
    this.reward_available = BuyBoosterDialog.isRewardAvailable( this.reward_source );
    this.effect_value = this.getConfiguredEffectValue();

    this.createElements();
  }

  private createElements(): void
  {
    this.createBackground();
    this.createTopBanner();
    this.createInfoFrame();
    this.createDescription();
    if ( this.reward_available )
      this.createRewardButton();
    this.createBuyButton();
    this.createCloseButton();
    this.coins_badge = new DialogCoinsBadge( this.ctx, this );
    this.addChild( this.coins_badge );
  }

  /** The rewarded (purple) button is offered only from the play dialog or in a level. */
  private static isRewardAvailable( source: BuyBoosterRewardSource ): boolean
  {
    return source === "game_start" || source === "in_game";
  }

  private static poolKeyFor( booster_id: CumulativeBoosterID, source: BuyBoosterRewardSource ): string
  {
    const reward_available = BuyBoosterDialog.isRewardAvailable( source );
    return `BuyBoosterDialog:${booster_id}:${reward_available ? "rv" : "plain"}`;
  }

  public static open( options: BuyBoosterDialogOptions ): BuyBoosterDialog
  {
    return DialogManager.openPooled(
      BuyBoosterDialog.poolKeyFor( options.booster_id, options.source ),
      options,
      () => new BuyBoosterDialog( options )
    ) as BuyBoosterDialog;
  }

  public override poolKey(): string
  {
    return BuyBoosterDialog.poolKeyFor( this.booster_id, this.reward_source );
  }

  public override isPoolable(): boolean { return true; }

  public override onReuse( options: BuyBoosterDialogOptions ): void
  {
    const reward_available = BuyBoosterDialog.isRewardAvailable( options.source );
    if ( options.booster_id !== this.booster_id || reward_available !== this.reward_available )
      throw new Error( "BuyBoosterDialog layout mismatch" );

    this.purchase_inflight = false;
    this.reward_inflight = false;
    this.reward_offer_shown_tracked = false;
    this.reward_offer_clicked = false;
    this.reward_granted = false;
    this.reward_offer_closed_tracked = false;
    this.reward_cooldown_visible = false;
    this.shown_reward_cooldown_timer = "";
    this.shown_ads_offline = false;
    this.next_reward_sync_at = 0;
    this.on_buy = options.on_buy;
    this.on_reward = options.on_reward;

    const content_changed = options.offer.id !== this.offer_id
      || options.offer.price !== this.price
      || options.offer.amount !== this.quantity
      || options.booster_type !== this.booster_type
      || options.source !== this.reward_source;
    this.price = options.offer.price;
    this.quantity = options.offer.amount;
    this.offer_id = options.offer.id;
    this.booster_type = options.booster_type;
    this.reward_source = options.source;
    this.rewarded_placement = `booster_${this.reward_source}_${this.visual_config.analytics_id}`;
    this.rewarded_cooldown_id = getRewardedCooldownId( this.booster_type );
    const effect_value = this.getConfiguredEffectValue();
    if ( content_changed || effect_value !== this.effect_value )
    {
      this.effect_value = effect_value;
      this.reward_btn = null;
      this.reward_active_content = null;
      this.reward_cooldown_content = null;
      this.reward_cooldown_text = null;
      this.rebuildChildren( () => this.createElements() );
    }
  }

  public override onPooled(): void
  {
    hideOfflineAdTooltip();
  }

  private getConfiguredEffectValue(): number | null
  {
    switch ( this.booster_id )
    {
      case CumulativeBoosterID.Multiball:
        return CumulativeBoosterService.getBoosterProperties(
          CumulativeBoosterID.Multiball
        )?.ball_count ?? getDefaultBoosterEffectValue( this.booster_type );
      case CumulativeBoosterID.InfiniteBalls:
        return CumulativeBoosterService.getBoosterProperties(
          CumulativeBoosterID.InfiniteBalls
        )?.duration_seconds ?? getDefaultBoosterEffectValue( this.booster_type );
      default:
        return getDefaultBoosterEffectValue( this.booster_type );
    }
  }

  private createBackground(): void
  {
    addMirroredDialogBg(
      this.ctx,
      this,
      BuyBoosterDialog.BG_X,
      BuyBoosterDialog.BG_Y,
      BuyBoosterDialog.BG_W,
      BuyBoosterDialog.BG_H
    );
  }

  private createTopBanner(): void
  {
    const design_w = 701;
    const design_h = 266;
    const design_x = ( BuyBoosterDialog.DESIGN_W - design_w ) / 2;
    const design_y = 408;

    const banner_w = LayoutScaler.widthFromDesign( design_w );
    const banner_h = LayoutScaler.heightFromDesign( design_h );
    const title_params = getBoosterTextVariableParams( this.booster_type, this.effect_value );
    const title_text = LocalizationManager.tWithParams( this.visual_config.title_key, title_params );
    const title_w = banner_w - LayoutScaler.widthFromDesign( 90 );
    const two_lines = this.titleNeedsTwoLines( title_text, title_w );
    const banner = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( design_x ),
      LayoutScaler.yFromDesign( design_y ),
      banner_w,
      banner_h,
      ImgID.UI_BoosterNameBg
    );

    if ( two_lines )
    {
      // Use two independent elements instead of relying on canvas wrapping.
      // UITextElement may horizontally scale a long line to fit, which can
      // visually collapse a forced newline back into a single-line title on
      // some layouts. Separate elements make the two-line layout invariant.
      const lines = splitIntoTwoBalancedLines( title_text ).split( "\n" );
      const line_h = LayoutScaler.heightFromDesign( 86 );
      lines.slice( 0, 2 ).forEach( ( line, index ) => banner.addChild( new UITextElement(
        this.ctx,
        LayoutScaler.xFromDesign( 45 ),
        LayoutScaler.yFromDesign( BuyBoosterDialog.TWO_LINE_TITLE_TOP_DESIGN + index * 86 ),
        title_w,
        line_h,
        {
          text: line,
          font_size: LayoutScaler.textSizeFromDesign( 62 ),
          font_weight: 900,
          text_color: ColorScheme.white,
          text_style: TextStyleScheme.geologica,
          text_align: MyTextAlign.Center,
          vertical_align: MyTextVerticalAlign.Center,
          outline_width: LayoutScaler.textBorderSizeFromDesign( 14 ),
          outline_color: ColorScheme.booster_dialog_title_outline,
          outline_offset_y: LayoutScaler.yFromDesign( 2 ),
          auto_wrap: false,
          fit_font_size: true,
          min_font_size: LayoutScaler.textSizeFromDesign( 48 ),
        }
      ) ) );
    }
    else
    {
      const title = new UITextElement(
        this.ctx,
        LayoutScaler.xFromDesign( 45 ),
        LayoutScaler.yFromDesign( 12 ),
        title_w,
        banner_h - LayoutScaler.heightFromDesign( 24 ),
        {
          localization_key: this.visual_config.title_key,
          text_variable_params: title_params,
          font_size: LayoutScaler.textSizeFromDesign( BuyBoosterDialog.TITLE_FONT_DESIGN ),
          font_weight: 900,
          text_color: ColorScheme.white,
          text_style: TextStyleScheme.geologica,
          text_align: MyTextAlign.Center,
          vertical_align: MyTextVerticalAlign.Center,
          outline_width: LayoutScaler.textBorderSizeFromDesign( BuyBoosterDialog.TITLE_OUTLINE_DESIGN ),
          outline_color: ColorScheme.booster_dialog_title_outline,
          outline_offset_y: LayoutScaler.yFromDesign( 2 ),
          auto_wrap: false,
          fit_font_size: true,
          min_font_size: LayoutScaler.textSizeFromDesign( 48 ),
          line_height: 0.95,
        }
      );
      banner.addChild( title );
    }
    this.addChild( banner );
  }

  /**
   * A title that fits the banner on one line ("Burst fire") stays there; a
   * longer one ("Залповий вогонь") goes to two balanced lines instead of
   * running into the banner edges. Measured with the one-line font and outline.
   */
  private titleNeedsTwoLines( title: string, max_width: number ): boolean
  {
    if ( title.trim().split( /\s+/ ).length < 2 ) return false;

    this.ctx.save();
    this.ctx.font = `normal 900 ${LayoutScaler.textSizeFromDesign( BuyBoosterDialog.TITLE_FONT_DESIGN )}px ${TextStyleScheme.geologica}`;
    const width = this.ctx.measureText( title ).width
      + LayoutScaler.textBorderSizeFromDesign( BuyBoosterDialog.TITLE_OUTLINE_DESIGN );
    this.ctx.restore();
    return width > max_width;
  }

  private createInfoFrame(): void
  {
    const design_w = 716;
    const design_h = 331;
    const design_x = ( BuyBoosterDialog.DESIGN_W - design_w ) / 2;
    const design_y = 678;

    const frame_w = LayoutScaler.widthFromDesign( design_w );
    const frame_h = LayoutScaler.heightFromDesign( design_h );
    const frame = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( design_x ),
      LayoutScaler.yFromDesign( design_y ),
      frame_w,
      frame_h,
      ImgID.UI_BoosterDialogFrameBg
    );

    const design_icon_size = 256;
    const icon_size = LayoutScaler.widthFromDesign( design_icon_size );
    const icon = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( ( design_w - design_icon_size ) / 2 ),
      LayoutScaler.yFromDesign( ( design_h - design_icon_size ) / 2 ),
      icon_size,
      icon_size,
      this.visual_config.icon_id
    );

    const quantity_text = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( 159 ),
      LayoutScaler.yFromDesign( 153.95 ),
      LayoutScaler.widthFromDesign( 84 ),
      LayoutScaler.heightFromDesign( 98 ),
      {
        text: `x${this.quantity}`,
        font_size: LayoutScaler.textSizeFromDesign( 70 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 14 ),
        outline_color: ColorScheme.booster_dialog_quantity_outline,
        outline_offset_y: LayoutScaler.yFromDesign( 1 ),
        auto_wrap: false,
      }
    );

    icon.addChild( quantity_text );
    frame.addChild( icon );
    this.addChild( frame );
  }

  private createDescription(): void
  {
    const description = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( 177 ),
      LayoutScaler.yFromDesign( 1022 ),
      LayoutScaler.widthFromDesign( 727 ),
      LayoutScaler.heightFromDesign( 183 ),
      {
        localization_key: this.visual_config.description_key,
        text_variable_params: getBoosterTextVariableParams( this.booster_type, this.effect_value ),
        font_size: LayoutScaler.textSizeFromDesign( 56 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 14 ),
        outline_color: ColorScheme.booster_dialog_quantity_outline,
        outline_offset_y: LayoutScaler.yFromDesign( 1 ),
        auto_wrap: true,
        fit_font_size: true,
        min_font_size: LayoutScaler.textSizeFromDesign( 38 ),
        line_height: 1.05,
      }
    );
    this.addChild( description );
  }

  private createBuyButton(): void
  {
    const design_x = BuyBoosterDialog.BTN_X;
    const design_y = BuyBoosterDialog.BUY_BTN_Y;
    const design_w = BuyBoosterDialog.BTN_W;
    const design_h = BuyBoosterDialog.BTN_H;

    const btn_w = LayoutScaler.widthFromDesign( design_w );
    const btn_h = LayoutScaler.heightFromDesign( design_h );
    const btn = new UINineSliceElement(
      this.ctx,
      LayoutScaler.xFromDesign( design_x ),
      LayoutScaler.yFromDesign( design_y ),
      btn_w,
      btn_h,
      ImgID.UI_GreenFrameBtn,
      "frame_btn",
      undefined,
      undefined,
      true
    );

    const text_options = {
      font_size: LayoutScaler.textSizeFromDesign( 60 ),
      min_font_size: LayoutScaler.textSizeFromDesign( 30 ),
      font_weight: 800,
      text_color: ColorScheme.white,
      text_style: TextStyleScheme.geologica,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      auto_wrap: false,
      scale_to_width: true,
      outline_color: ColorScheme.more_lives_buy_outline,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
      outline_offset_y: LayoutScaler.yFromDesign( 2 ),
      ...TextStyleScheme.buttonShadow(),
    };

    const side_padding = LayoutScaler.widthFromDesign( 45 );
    const content_w = btn_w - side_padding * 2;

    const buy_text = new UITextElement(
      this.ctx, 0, 0, content_w, btn_h,
      { localization_key: "BUY", ...text_options }
    );
    const coin_size = LayoutScaler.widthFromDesign( 104 );
    const coin = new UIImage(
      this.ctx, 0, 0,
      coin_size, coin_size, ImgID.IconCoin
    );
    const price_text = new UITextElement(
      this.ctx, 0, 0, content_w, btn_h,
      {
        text: this.price.toString(),
        ...text_options,
        font_size: LayoutScaler.textSizeFromDesign( 64 ),
        font_weight: 900,
      }
    );
    const content = new UIAdaptiveElementsByTextHorisontal(
      this.ctx, side_padding, 0, content_w, btn_h,
      [ buy_text, coin, price_text ], LayoutScaler.widthFromDesign( 14 )
    );
    btn.addChild( content, true );
    btn.setOnClick( async () =>
    {
      if ( StatisticsStorage.load().coins < this.price )
      {
        // Keep the current game and this dialog alive under the coin shop.
        // Closing the shop returns the player to the exact same game state.
        OverlayFactory.openOverlay( OverlayId.SHOP_OVERLAY, {
          onShopClosed: () => {
            // ShopScreen has already synchronized the purchased coins.
            // The next tap reads the fresh value from StatisticsStorage.
          },
        } );
        return;
      }

      if ( this.purchase_inflight )
        return;

      this.purchase_inflight = true;
      const response = await CumulativeBoosterService.purchase(
        this.booster_id,
        this.offer_id
      );

      if ( response === null )
      {
        // The outcome of a network timeout can be ambiguous: the server may
        // already have committed the purchase. Close the locked dialog instead
        // of allowing an unsafe immediate retry.
        this.close( "buy_booster_failed" );
        return;
      }

      // Keep the button locked for the close animation. Resetting the flag
      // here allowed a second tap to buy the same pack again (3 -> 6).
      this.close( "buy_booster" );
      this.on_buy?.( response );
    } );
    this.addChild( btn );
  }

  private createRewardButton(): void
  {
    const design_x = BuyBoosterDialog.BTN_X;
    const design_y = BuyBoosterDialog.REWARD_BTN_Y;
    const design_w = BuyBoosterDialog.BTN_W;
    const design_h = BuyBoosterDialog.BTN_H;
    const design_content_dx = ( design_w - 670 ) / 2;
    const design_content_dy = ( design_h - 250 ) / 2;

    const btn_w = LayoutScaler.widthFromDesign( design_w );
    const btn_h = LayoutScaler.heightFromDesign( design_h );
    const btn = new UINineSliceElement(
      this.ctx,
      LayoutScaler.xFromDesign( design_x ),
      LayoutScaler.yFromDesign( design_y ),
      btn_w,
      btn_h,
      ImgID.UI_PurpleFrameBtn,
      "frame_btn",
      undefined,
      undefined,
      true
    );

    const video_icon = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( 84 + design_content_dx ),
      LayoutScaler.yFromDesign( 85 + design_content_dy ),
      LayoutScaler.widthFromDesign( 104 ),
      LayoutScaler.heightFromDesign( 93 ),
      ImgID.UI_VideoIcon
    );
    const reward_text = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( 190 + design_content_dx ),
      LayoutScaler.yFromDesign( 28 + design_content_dy ),
      LayoutScaler.widthFromDesign( 420 ),
      LayoutScaler.heightFromDesign( 210 ),
      {
        localization_key: "BUY_ONE_ADS",
        text_variable_params: [ String( this.reward_quantity ) ],
        font_size: LayoutScaler.textSizeFromDesign( 53 ),
        min_font_size: LayoutScaler.textSizeFromDesign( 40 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: true,
        fit_font_size: true,
        line_height: 0.95,
        outline_color: ColorScheme.more_lives_purple_outline,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
        outline_offset_y: LayoutScaler.yFromDesign( 2 ),
        ...TextStyleScheme.buttonShadow(),
      }
    );

    const active_content = new UIElementHolder( this.ctx, 0, 0, btn_w, btn_h );
    active_content.addChild( video_icon, true );
    active_content.addChild( reward_text, true );
    const clock_size = LayoutScaler.widthFromDesign( 104 );
    const clock = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( 84 + design_content_dx ),
      ( btn_h - clock_size ) / 2,
      clock_size,
      clock_size,
      ImgID.UI_ClockFrame
    );
    const hand_h = LayoutScaler.heightFromDesign( 80 );
    const hand_w = hand_h * ( 24 / 73 );
    const hand = new UIImage(
      this.ctx,
      ( clock_size - hand_w ) / 2,
      ( clock_size - hand_h ) / 2,
      hand_w,
      hand_h,
      ImgID.UI_ClockHand
    );
    hand.enableRotation();
    hand.setRotationImmediate( Math.PI / 6 );
    clock.addChild( hand );

    const cooldown_text = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( 225 + design_content_dx ),
      LayoutScaler.yFromDesign( 75 + design_content_dy ),
      LayoutScaler.widthFromDesign( 350 ),
      LayoutScaler.heightFromDesign( 100 ),
      {
        text: "00:00:00",
        font_size: LayoutScaler.textSizeFromDesign( 60 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        scale_to_width: true,
        ...TextStyleScheme.buttonShadow(),
      }
    );
    const cooldown_content = new UIElementHolder( this.ctx, 0, 0, btn_w, btn_h );
    cooldown_content.addChild( clock, true );
    cooldown_content.addChild( cooldown_text, true );
    active_content.setClickable( false );
    cooldown_content.setClickable( false );
    btn.addChild( active_content, true );

    btn.setOnClick( () => { void this.watchRewardedAd( btn ); } );
    this.addChild( btn );
    this.reward_btn = btn;
    this.reward_active_content = active_content;
    this.reward_cooldown_content = cooldown_content;
    this.reward_cooldown_text = cooldown_text;
    this.reward_text = reward_text;
    this.shown_reward_inactive = null;
    setButtonTextOutline( [ cooldown_text ], false, ColorScheme.more_lives_purple_outline );
    this.syncRewardButtonState();
  }

  public override update( now: number ): void
  {
    super.update( now );
    this.coins_badge?.sync( now );
    if ( now < this.next_reward_sync_at ) return;
    this.next_reward_sync_at = now + 250;
    this.syncRewardButtonState();
  }

  private syncRewardButtonState(): void
  {
    if ( !this.reward_btn || !this.reward_active_content || !this.reward_cooldown_content )
      return;

    const remaining = rewardedCooldownRemainingMs( this.rewarded_cooldown_id );
    const cooldown_active = remaining > 0;
    const timer_text = rewardedCooldownTimerText( remaining );

    if ( timer_text !== this.shown_reward_cooldown_timer )
    {
      this.shown_reward_cooldown_timer = timer_text;
      this.reward_cooldown_text?.setText( timer_text );
      this.reward_debug_render_reason = `timer:${timer_text}`;
      this.invalidateSubtree();
    }

    if ( cooldown_active !== this.reward_cooldown_visible )
    {
      this.reward_cooldown_visible = cooldown_active;
      if ( cooldown_active )
      {
        this.reward_btn.removeChild( this.reward_active_content );
        this.reward_btn.addChild( this.reward_cooldown_content, true );
      }
      else
      {
        this.reward_btn.removeChild( this.reward_cooldown_content );
        this.reward_btn.addChild( this.reward_active_content, true );
      }
      this.reward_debug_render_reason = `state:${cooldown_active ? 'cooldown' : 'available'}`;
      this.invalidateSubtree();
    }

    const disabled = cooldown_active || this.reward_inflight;
    const ads_offline = areAdsBlockedOffline();
    const inactive = disabled || ads_offline;
    this.reward_btn.setClickable( !disabled );
    this.reward_btn.setImgId(
      inactive ? ImgID.UI_NonActiveBtn : ImgID.UI_PurpleFrameBtn,
      inactive ? "non_active_btn" : "frame_btn"
    );
    if ( inactive !== this.shown_reward_inactive )
    {
      this.shown_reward_inactive = inactive;
      setButtonTextOutline( [ this.reward_text ], !inactive, ColorScheme.more_lives_purple_outline );
      this.invalidateSubtree();
    }
    if ( ads_offline !== this.shown_ads_offline )
    {
      this.shown_ads_offline = ads_offline;
      if ( !ads_offline ) hideOfflineAdTooltip();
      this.invalidateSubtree();
    }
  }

  public override cleanup(): void
  {
    hideOfflineAdTooltip();
    if ( this.reward_btn && this.reward_active_content && this.reward_cooldown_content )
    {
      this.reward_btn.removeChild( this.reward_active_content );
      this.reward_btn.removeChild( this.reward_cooldown_content );
      this.reward_active_content.cleanup();
      this.reward_cooldown_content.cleanup();
    }
    super.cleanup();
  }

  private async watchRewardedAd( btn: UINineSliceElement ): Promise<void>
  {
    if ( this.reward_inflight || isRewardedCooldownActive( this.rewarded_cooldown_id ) )
      return;

    if ( areAdsBlockedOffline() )
    {
      showOfflineAdTooltip( btn );
      return;
    }

    this.reward_inflight = true;
    this.reward_offer_clicked = true;
    btn.setImgId( ImgID.UI_NonActiveBtn, "non_active_btn" );

    void this.trackRewardedFunnelEvent( AnalyticsEventName.BOOSTER_REWARD_OFFER_CLICKED );

    let reward_earned = false;
    try
    {
      const result = await showRewarded( this.rewarded_placement );
      if ( !result.rewarded )
      {
        void this.trackRewardedFunnelEvent( AnalyticsEventName.BOOSTER_REWARD_AD_RESULT, {
          status: result.refused ? "skipped" : "unavailable",
          shown: result.shown,
          rewarded: false,
        } );
        if ( !result.shown ) showAdUnavailableTooltip( btn );
        return;
      }

      void this.trackRewardedFunnelEvent( AnalyticsEventName.BOOSTER_REWARD_AD_RESULT, {
        status: "completed",
        shown: true,
        rewarded: true,
      } );
      reward_earned = true;

      if ( !result.ad_id )
        throw new Error( "Rewarded ad completed without an ad id" );
      const reward = await this.rewardBoosterWithRetry( result.ad_id );
      adoptEconomyFromPlayer( reward.player );
      const quantity = reward.granted.quantity;
      startRewardedCooldown( this.rewarded_cooldown_id );
      this.on_reward?.( quantity );

      this.reward_granted = true;
      void this.trackRewardedFunnelEvent( AnalyticsEventName.BOOSTER_REWARD_GRANTED, {
        status: "granted",
        shown: true,
        rewarded: true,
        idempotent: reward.idempotent,
      } );

      void logRewardedCompleted( {
        placement: this.rewarded_placement,
        reward_type: this.visual_config.analytics_id,
        reward_value: quantity,
      } );
      this.close( "buy_booster_rewarded" );
    }
    catch ( e )
    {
      console.warn( "[BuyBoosterDialog] rewarded booster failed", e );
      void this.trackRewardedFunnelEvent( AnalyticsEventName.BOOSTER_REWARD_AD_RESULT, {
        status: reward_earned ? "grant_error" : "error",
        rewarded: reward_earned,
        ...( reward_earned ? { shown: true } : {} ),
      } );
    }
    finally
    {
      this.reward_inflight = false;
      this.syncRewardButtonState();
    }
  }

  private async rewardBoosterWithRetry( ad_id: string ): Promise<RewardBoosterResponse>
  {
    let last_error: unknown;
    for ( let attempt = 0; attempt < 2; attempt++ )
    {
      try
      {
        return await rewardBooster(
          this.visual_config.analytics_id,
          this.rewarded_placement,
          ad_id
        );
      }
      catch ( error )
      {
        last_error = error;
        if ( attempt === 0 )
          await new Promise( resolve => window.setTimeout( resolve, 300 ) );
      }
    }

    throw last_error;
  }

  private trackRewardedFunnelEvent(
    event_name:
      | AnalyticsEventName.BOOSTER_REWARD_OFFER_SHOWN
      | AnalyticsEventName.BOOSTER_REWARD_OFFER_CLICKED
      | AnalyticsEventName.BOOSTER_REWARD_AD_RESULT
      | AnalyticsEventName.BOOSTER_REWARD_GRANTED
      | AnalyticsEventName.BOOSTER_REWARD_OFFER_CLOSED,
    extra: {
      status?: string;
      close_reason?: string;
      shown?: boolean;
      rewarded?: boolean;
      idempotent?: boolean;
    } = {}
  ): Promise<void>
  {
    return logBoosterRewardedFunnelEvent( event_name, {
      placement: this.rewarded_placement,
      source: this.reward_source,
      booster_type: this.visual_config.analytics_id,
      reward_value: this.reward_quantity,
      ...extra,
    } );
  }

  public override onDialogOpened(): void
  {
    super.onDialogOpened();
    if ( !this.reward_available || this.reward_offer_shown_tracked )
      return;

    this.reward_offer_shown_tracked = true;
    void this.trackRewardedFunnelEvent( AnalyticsEventName.BOOSTER_REWARD_OFFER_SHOWN );
  }

  public override onDialogCloseRequested( reason: string ): void
  {
    if ( !this.reward_available || this.reward_offer_closed_tracked )
      return;

    this.reward_offer_closed_tracked = true;
    void this.trackRewardedFunnelEvent( AnalyticsEventName.BOOSTER_REWARD_OFFER_CLOSED, {
      status: this.reward_granted
        ? "reward_granted"
        : this.reward_offer_clicked ? "after_click" : "without_click",
      close_reason: reason,
    } );
  }

  private createCloseButton(): void
  {
    const design_size = 106;
    const btn = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( 892 ),
      LayoutScaler.yFromDesign( 391 ),
      LayoutScaler.widthFromDesign( design_size ),
      LayoutScaler.heightFromDesign( design_size ),
      ImgID.UI_SettingsDialogCloseBtn,
      undefined,
      true
    );
    btn.setOnClick( () => this.close( "close_button" ) );
    this.addChild( btn );
    this.close_btn = btn;
  }

  protected override playAppearAnimation(
    from_scale = 0.7,
    overshoot_scale = 1.1,
    final_scale = 1.0,
    scale_up_duration = 136,
    settle_duration = 96
  ): void
  {
    this.close_btn.setScale( new Vector2( 0, 0 ) );
    super.playAppearAnimation(
      from_scale,
      overshoot_scale,
      final_scale,
      scale_up_duration,
      settle_duration
    );

    this.popCloseButtonAfterAppear( this.close_btn, scale_up_duration + settle_duration );
  }

  public override containsPointGlobal( p: Vector2 ): boolean
  {
    const body_pos = this.pixelPosForRender();
    const body_scale = this.getScaleImportant();
    const body_center_x = body_pos.x + this.rect.getW() / 2;
    const body_center_y = body_pos.y + this.rect.getH() / 2;

    const hit_x = LayoutScaler.xFromDesign( BuyBoosterDialog.HIT_X );
    const hit_y = LayoutScaler.yFromDesign( BuyBoosterDialog.HIT_Y );
    const hit_w = LayoutScaler.widthFromDesign( BuyBoosterDialog.HIT_W );
    const hit_h = LayoutScaler.heightFromDesign( BuyBoosterDialog.HIT_H );

    const left = body_center_x + ( hit_x - this.rect.getW() / 2 ) * body_scale.x;
    const top = body_center_y + ( hit_y - this.rect.getH() / 2 ) * body_scale.y;
    const right = left + hit_w * body_scale.x;
    const bottom = top + hit_h * body_scale.y;

    return p.x >= left && p.x <= right && p.y >= top && p.y <= bottom;
  }

  public name(): string
  {
    return "BuyBoosterDialog";
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    if ( this.reward_debug_render_reason )
    {
      this.reward_debug_render_reason = null;
    }

    this.startRender();
    this.finishRender();
  }
}
